import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { API } from "aws-amplify";
import { GET_CHECKLIST_FOR_EQUIPMENT } from "../../gql/bookingapi";
import { REPO_CONFIG } from "../../constants";
import { configureAwsAppSync } from "../../utils/helpers/configureAwsAppSync";
import DocumentationModal from "./DocumentationModal";
import ChecklistFolder from "../../icons/ChecklistFolder";
import { getFieldValue } from "../booking-instruments/helpers";

export const documentationCache = new Map();

const resolveField = (data, snakeKey, camelKey) =>
  getFieldValue(data, snakeKey) ||
  getFieldValue(data?.equipment, camelKey) ||
  getFieldValue(data, camelKey);

const documentationRequests = new Map();

const requestDocumentation = async ({ model, site, group, cacheKey }) => {
  try {
    // No await between configure and graphql: Amplify reads the global endpoint synchronously inside graphql().
    configureAwsAppSync({ aws_appsync_graphqlEndpoint: REPO_CONFIG.aws_appsync_graphqlEndpoint });
    const result = await API.graphql({
      query: GET_CHECKLIST_FOR_EQUIPMENT,
      variables: { input: { model, site, group } },
    });
    const items = result?.data?.getChecklistsForEquipment?.items || [];
    documentationCache.set(cacheKey, items);
    return items;
  } catch (error) {
    console.warn("Documentation fetch failed:", error);
    return [];
  }
};

export const fetchDocumentationStatus = async ({ model, site, group }) => {
  const cacheKey = `${site}#${group}#${model}`;
  const cached = documentationCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }
  if (documentationRequests.has(cacheKey)) {
    return documentationRequests.get(cacheKey);
  }

  const request = requestDocumentation({ model, site, group, cacheKey }).finally(() =>
    documentationRequests.delete(cacheKey)
  );
  documentationRequests.set(cacheKey, request);
  return request;
};

const DocumentationRender = (params) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const checkedRef = useRef(false);

  const { data } = params || {};
  const model = resolveField(data, "equipment_model", "equipmentModel");
  const site = resolveField(data, "site_name", "siteName");
  const group = resolveField(data, "belonging_to_group", "belongingToGroup");

  useEffect(() => {
    if (checkedRef.current || !model || !site || !group) {
      return;
    }
    checkedRef.current = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        const items = await fetchDocumentationStatus({ model, site, group });
        setDocuments(items);
      } catch (error) {
        console.warn("Documentation fetch failed:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [model, site, group]);

  if (loading || !documents?.length) {
    return "-";
  }

  const handleOpenModal = (e) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  const handleCloseModal = (e) => {
    e.stopPropagation();
    setModalOpen(false);
  };

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <button
        onClick={handleOpenModal}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
        }}
        title={`Show documentation for ${model}`}
      >
        <ChecklistFolder />
      </button>

      {modalOpen &&
        ReactDOM.createPortal(
          <DocumentationModal
            visible={modalOpen}
            onClose={handleCloseModal}
            equipmentDetails={data?.equipment || data}
            documents={documents}
          />,
          document.body
        )}
    </div>
  );
};

export default DocumentationRender;
